import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  Paper,
  useTheme,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Code as CodeIcon,
  Speed as SpeedIcon,
  Verified as VerifiedIcon,
  Groups as GroupsIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';

const AboutPage: React.FC = () => {
  const theme = useTheme();

  const founders = [
    {
      name: 'Daniel Suissa',
      role: 'CEO & Technical Lead',
      description: 'Former Israeli military cyber professional with extensive experience in security-focused software development and AI systems.',
      initials: 'DS',
    },
    {
      name: 'Daniel Feldman',
      role: 'Business Development & Marketing',
      description: 'Years of experience in business development, marketing, and strategic growth, driving partnerships and market expansion.',
      initials: 'DF',
    },
  ];

  const values = [
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: 'white' }} />,
      title: 'Security First',
      description: 'We embed security into every line of code from the design phase, not as an afterthought.',
      color: 'primary.main',
    },
    {
      icon: <VerifiedIcon sx={{ fontSize: 40, color: 'white' }} />,
      title: 'Compliance Ready',
      description: 'Our tools generate code that meets organizational standards and regulatory requirements.',
      color: 'secondary.main',
    },
    {
      icon: <CodeIcon sx={{ fontSize: 40, color: 'white' }} />,
      title: 'Code Quality',
      description: 'We believe code is just the starting point. Maintainability and best practices are built-in.',
      color: 'success.main',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: 'white' }} />,
      title: 'Fast Delivery',
      description: 'Enable rapid development without sacrificing quality, security, or compliance.',
      color: 'warning.main',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 2,
              fontSize: '0.875rem',
              mb: 3,
            }}>
              About Loxia
            </Box>
            <Typography
              variant="h2"
              component="h1"
              fontWeight="bold"
              sx={{
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 3,
              }}
            >
              Bridging the Gap in AI-Powered Development
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                maxWidth: 800,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Loxia is transforming how companies adopt AI in software development by providing
              tools that generate secure, compliant, and organization-standards-driven code.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Mission Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Paper
          sx={{
            p: { xs: 3, md: 5 },
            bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.paper',
          }}
        >
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Box sx={{
              bgcolor: 'primary.main',
              width: 48,
              height: 48,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <LightbulbIcon sx={{ color: 'white' }} />
            </Box>
            <Typography variant="h4" fontWeight="bold">
              Our Mission
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            We aim to empower companies of all sizes, as well as solo entrepreneurs, with the ability
            to deliver AI-generated code that makes customer-facing products safe and easy to deliver
            and operate. We understand that writing code is merely the starting point of a product's
            lifecycle. That's why we provide solutions that make the development process concise and
            focused on what matters most: security, maintainability, compliance, and monitoring—
            all approached from the design phase rather than as code revisits.
          </Typography>
        </Paper>
      </Container>

      {/* Values Section */}
      <Box sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.paper', py: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography variant="h3" component="h2" fontWeight="bold" gutterBottom>
              What We Stand For
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Our core values guide everything we build
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                  <Box sx={{
                    bgcolor: value.color,
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2
                  }}>
                    {value.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom fontWeight="600">
                    {value.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {value.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Founders Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2}>
            <Box sx={{
              bgcolor: 'secondary.main',
              width: 48,
              height: 48,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <GroupsIcon sx={{ color: 'white' }} />
            </Box>
            <Typography variant="h3" component="h2" fontWeight="bold">
              Meet the Founders
            </Typography>
          </Box>
          <Typography variant="h6" color="text.secondary">
            The team behind Autopilot One
          </Typography>
        </Box>
        <Grid container spacing={4} justifyContent="center">
          {founders.map((founder, index) => (
            <Grid item xs={12} sm={6} md={5} key={index}>
              <Card sx={{ p: 4, height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      fontSize: '1.75rem',
                      fontWeight: 700,
                      bgcolor: index === 0 ? 'primary.main' : 'secondary.main',
                    }}
                  >
                    {founder.initials}
                  </Avatar>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {founder.name}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="primary.main"
                    fontWeight="600"
                    sx={{ mb: 2 }}
                  >
                    {founder.role}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                    {founder.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Call to Action */}
      <Box sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.paper', py: 8 }}>
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Ready to Transform Your Development Process?
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Join companies worldwide using Loxia to build secure, compliant software faster.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutPage;
